// src/pages/announcements/announcementDetails/announcementDetails.js
import React, { useEffect, useReducer, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { Button, DatePicker, Form, Input, Modal, Select, TimePicker } from "antd";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import useNotification from "../../../hooks/useNotification";
import Layout from "../../../components/layout/layout";
import "./announcementDetails.css";

const { TextArea } = Input;

const CATEGORY_OPTIONS = [
  { value: "general", label: "General" },
  { value: "worship", label: "Worship" },
  { value: "events", label: "Events" },
  { value: "ministry", label: "Ministry" },
  { value: "prayer", label: "Prayer" },
  { value: "community", label: "Community" }
];

// Initial state for useReducer
const initialState = {
  showConfirmDialog: false,
  isDeleting: false,
  initialValues: {},
  formValues: {}
};

// Reducer for state management
const announcementReducer = (state, action) => {
  switch (action.type) {
    case "setShowConfirmDialog":
      return { ...state, showConfirmDialog: action.payload };
    case "setIsDeleting":
      return { ...state, isDeleting: action.payload };
    case "setInitialValues":
      return { ...state, initialValues: action.payload };
    case "setFormValues":
      return { ...state, formValues: action.payload };
    default:
      return state;
  }
};

function AnnouncementDetails() {
  const { notification, showNotification, hideNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const announcement = location.state;
  const isEditMode = Boolean(announcement);
  const [form] = Form.useForm();
  const [state, dispatch] = useReducer(announcementReducer, initialState);
  const { showConfirmDialog, isDeleting, initialValues, formValues } = state;

  // Check if form has been modified from initial values (edit mode)
  const isFormModified = useCallback(() => {
    const currentValues = form.getFieldsValue();
    return Object.keys(currentValues).some((key) => {
      const current = currentValues[key];
      const initial = initialValues[key];

      if (current && current._isADayJSObject && initial && initial._isADayJSObject) {
        return !current.isSame(initial);
      }

      return current !== initial;
    });
  }, [form, initialValues]);

  // Save button enabled logic
  const isSaveButtonEnabled = useMemo(() => {
    if (isEditMode) {
      return isFormModified();
    }
    const hasTitle = formValues.title?.trim();
    const hasDescription = formValues.description?.trim();
    return Boolean(hasTitle && hasDescription);
  }, [isEditMode, isFormModified, formValues]);

  // Handle form field changes
  const handleValuesChange = useCallback((_, allValues) => {
    dispatch({ type: "setFormValues", payload: allValues });
  }, []);

  // Save / Update
  const handleSave = useCallback(async () => {
    try {
      await form.validateFields(["title", "description"]);
      const values = form.getFieldsValue();

      const payload = {
        topic: "announcements",
        title: values.title,
        body: values.description,
        category: values.category || undefined,
        date: values.date ? values.date.format("YYYY-MM-DD") : undefined,
        time: values.time ? values.time.format("HH:mm") : undefined
      };

      const token = await getAuthToken();

      if (isEditMode) {
        const res = await fetch(API_ENDPOINTS.NOTIFICATIONS, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ id: announcement?.id, ...payload })
        });

        if (res.ok) {
          showNotification("Announcement updated successfully!", "success");
          setTimeout(() => navigate("/announcementList"), 1000);
        } else {
          const errorData = await res.json().catch(() => null);
          const detail = errorData?.message || res.statusText || "Unknown error";
          showNotification(`Error ${res.status}: ${detail}`, "error");
        }
      } else {
        const res = await fetch(API_ENDPOINTS.NOTIFICATIONS, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });

        if (res.ok) {
          showNotification("Announcement created successfully!", "success");
          setTimeout(() => navigate("/announcementList"), 1000);
        } else {
          const errorData = await res.json().catch(() => null);
          const detail = errorData?.message || res.statusText || "Unknown error";
          showNotification(`Error ${res.status}: ${detail}`, "error");
        }
      }
    } catch (error) {
      if (error.errorFields) {
        showNotification("Please fill in all required fields", "error");
      } else {
        showNotification(`Network error: ${error.message}`, "error");
      }
    }
  }, [form, isEditMode, announcement, navigate, showNotification]);

  // Delete confirmation
  const handleConfirmDelete = useCallback(async () => {
    dispatch({ type: "setIsDeleting", payload: true });
    const title = form.getFieldValue("title");

    try {
      const token = await getAuthToken();
      await axios.delete(API_ENDPOINTS.NOTIFICATIONS, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        data: {
          id: announcement?.id,
          title
        }
      });

      showNotification(`Announcement "${title}" deleted successfully.`, "success");
      dispatch({ type: "setShowConfirmDialog", payload: false });
      setTimeout(() => navigate("/announcementList"), 1000);
    } catch (error) {
      console.error("Delete failed:", error);
      showNotification("Failed to delete announcement: " + (error.response?.data?.message || error.message), "error");
    } finally {
      dispatch({ type: "setIsDeleting", payload: false });
    }
  }, [form, announcement, navigate, showNotification]);

  // Cancel
  const handleCancel = useCallback(() => {
    navigate("/announcementList");
  }, [navigate]);

  // Initialise form in edit mode
  useEffect(() => {
    if (announcement) {
      const date = announcement.publishDate || announcement.createdAt || announcement.created_at;

      const formVals = {
        title: announcement.title || "",
        description: announcement.body || announcement.description || "",
        category: announcement.category || undefined,
        date: date ? dayjs(date) : null,
        time: announcement.time ? dayjs(announcement.time, "HH:mm") : null
      };

      form.setFieldsValue(formVals);
      dispatch({ type: "setInitialValues", payload: formVals });
      dispatch({ type: "setFormValues", payload: formVals });
    } else {
      const now = new Date();
      const emptyVals = {
        title: "",
        description: "",
        category: undefined,
        date: dayjs(now),
        time: dayjs(now.toTimeString().slice(0, 5), "HH:mm")
      };

      form.setFieldsValue(emptyVals);
      dispatch({ type: "setInitialValues", payload: emptyVals });
      dispatch({ type: "setFormValues", payload: emptyVals });
    }
  }, [announcement, form]);

  // Memoise delete modal title
  const deleteModalTitle = useMemo(
    () => form.getFieldValue("title"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, showConfirmDialog]
  );

  return (
    <Layout
      navTitle="Announcements"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="amd-form-container">
        <div className="amd-form-wrapper">
          <Form form={form} layout="vertical" onValuesChange={handleValuesChange} className="amd-antd-form">
            {/* Announcement Details */}
            <div className="amd-form-section">
              <h3 className="amd-section-title">Announcement Details</h3>

              <Form.Item label="Announcement Title" name="title" rules={[{ required: true, message: "Please enter a title" }]}>
                <Input placeholder="Enter announcement title" />
              </Form.Item>

              <Form.Item label="Announcement Description" name="description" rules={[{ required: true, message: "Please enter a description" }]}>
                <TextArea rows={5} placeholder="Enter announcement description" />
              </Form.Item>

              <Form.Item label="Category" name="category">
                <Select placeholder="Select category" allowClear options={CATEGORY_OPTIONS} />
              </Form.Item>
            </div>

            {/* Date & Time */}
            <div className="amd-form-section">
              <h3 className="amd-section-title">Date & Time</h3>
              <div className="amd-form-row">
                <Form.Item label="Date" name="date">
                  <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                </Form.Item>

                <Form.Item label="Time" name="time">
                  <TimePicker style={{ width: "100%" }} format="HH:mm" use12Hours={false} />
                </Form.Item>
              </div>
            </div>

            {/* Action buttons */}
            <div className="amd-form-btn-group">
              <Button className="amd-form-cancel-btn" onClick={handleCancel}>
                Cancel
              </Button>

              {isEditMode && (
                <Button className="amd-form-delete-btn" onClick={() => dispatch({ type: "setShowConfirmDialog", payload: true })}>
                  Delete
                </Button>
              )}

              <Button className="amd-form-save-btn" onClick={handleSave} disabled={!isSaveButtonEnabled}>
                {isEditMode ? "Update" : "Save"}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <Modal
        title="Confirm Delete"
        open={showConfirmDialog}
        onOk={handleConfirmDelete}
        onCancel={() => dispatch({ type: "setShowConfirmDialog", payload: false })}
        okText={isDeleting ? "Deleting..." : "Yes"}
        cancelText="No"
        okButtonProps={{ danger: true, disabled: isDeleting }}
        cancelButtonProps={{ disabled: isDeleting }}
      >
        <p>Are you sure you want to delete this announcement?</p>
        <p>
          <strong>"{deleteModalTitle}"</strong>
        </p>
      </Modal>
    </Layout>
  );
}

export default AnnouncementDetails;
